import { BtnClasses, Popup } from '@tcweb/connect-ui';
import {
  FX_EXTRACTION_WORKFLOWS,
  FxExtractionWorkflow,
  useFxWorkspaceContext,
} from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import { Component, For, JSX, Show, createSignal } from 'solid-js';
import { Portal } from 'solid-js/web';

export interface NewExtractionDialogProps {
  readonly show: boolean;
}

export const NewExtractionDialog: Component<NewExtractionDialogProps> = (
  props
) => {
  return (
    <Show when={props.show}>
      <NewExtractionDialogContent />
    </Show>
  );
};

const NewExtractionDialogContent: Component = () => {
  const fxWorkspace = useFxWorkspaceContext();
  const [selectedWorkflow, setSelectedWorkflow] = createSignal<
    FxExtractionWorkflow | undefined
  >(fxWorkspace.selectedExtractionWorkflow());

  const handleClose = (): void => {
    fxWorkspace.closeNewExtractionDialog();
  };

  const handleContinue = (): void => {
    const workflow = selectedWorkflow();
    if (workflow === undefined) {
      return;
    }

    fxWorkspace.confirmExtractionWorkflowSelection(workflow);
  };

  const pickerContent = (): JSX.Element => (
    <div class="flex flex-col gap-3" data-testid="extraction-workflow-picker">
      <For each={FX_EXTRACTION_WORKFLOWS}>
        {(workflow) => (
          <label
            class="flex cursor-pointer items-start gap-2"
            for={`extraction-workflow-${workflow}`}
            data-testid={`extraction-workflow-${workflow}`}
            onClick={() => setSelectedWorkflow(workflow)}
          >
            <modus-wc-radio
              size="sm"
              class="mt-0.5 shrink-0"
              name="fx-extraction-workflow"
              input-id={`extraction-workflow-${workflow}`}
              value={selectedWorkflow() === workflow}
              on:inputChange={() => setSelectedWorkflow(workflow)}
            />
            <div class="flex flex-col flex-1 min-w-0">
              <span class="m-0 text-sm font-semibold">
                {t(
                  `mobileMapping.fxWorkspace.extraction.workflow.${workflow}.label`
                )}
              </span>
              <span class="text-sm text-muted">
                {t(
                  `mobileMapping.fxWorkspace.extraction.workflow.${workflow}.description`
                )}
              </span>
            </div>
          </label>
        )}
      </For>
    </div>
  );

  return (
    <Portal>
      <Popup
        show={true}
        name="new-fx-extraction"
        heading={t('mobileMapping.fxWorkspace.extraction.newExtractionTitle')}
        subHeading={t('mobileMapping.fxWorkspace.extraction.selectWorkflow')}
        role="dialog"
        aria-modal={true}
        overlayClass="z-modal"
        closeOnBackdropClick={true}
        content={pickerContent()}
        secondaryBtnLabel={t('general.cancel')}
        secondaryBtnClass={BtnClasses.SECONDARY}
        onSecondaryAction={handleClose}
        primaryBtnLabel={t('general.next')}
        primaryBtnClass={BtnClasses.PRIMARY}
        primaryBtnDisabled={selectedWorkflow() === undefined}
        onPrimaryAction={handleContinue}
        onClose={handleClose}
      />
    </Portal>
  );
};
